import {
  SourceCreateRequestDto, SourceCreateResponseDto, SourceGetAllRequestDto,
  SourceGetAllResponseDto, SourceGetRequestDto,
  SourceGetResponseDto,
  SourceUpdateRequestDto, SourceUpdateResponseDto
} from '../../dtos/source.dto';
import {SourceListModel, SourceModel} from '../../models/source.model';
import {createMap, forMember, mapFrom, MappingProfile} from '@automapper/core';
import {
  HighlightCreateRequestDto, HighlightCreateResponseDto,
  HighlightGetAllResponseDto,
  HighlightGetResponseDto,
  HighlightUpdateRequestDto, HighlightUpdateResponseDto
} from '../../dtos/highlight.dto';
import {HighlightListModel, HighlightModel} from '../../models/highlight.model';
import {
  ReminderLogCreateRequestDto, ReminderLogGetAllResponseDto,
  ReminderLogGetResponseDto,
  ReminderLogUpdateRequestDto
} from '../../dtos/reminder-log.dto';
import {ReminderLogListModel, ReminderLogModel} from '../../models/reminder-log.model';
import {
  UserCreateRequestDto,
  UserGetAllResponseDto,
  UserGetResponseDto,
  UserUpdateRequestDto
} from '../../dtos/user.dto';
import {UserListModel, UserModel} from '../../models/user.model';
import {
  UserSettingCreateRequestDto, UserSettingCreateResponseDto,
  UserSettingGetAllResponseDto,
  UserSettingGetResponseDto,
  UserSettingUpdateRequestDto, UserSettingUpdateResponseDto
} from '../../dtos/user-setting.dto';
import {UserSettingListModel, UserSettingModel} from '../../models/user-setting.model';
import {UserLocalDataModel} from '../../models/user-local-data.model';
import {SignUpRequestDto, SignUpResponseDto} from '../../dtos/authentication/sign-up.dto';
import {RefreshTokenResponseDto} from '../../dtos/refresh-token.dto';
import {SignUpRequestModel, SignUpResponseModel} from '../../models/authentication/sign-up.model';
import {SourceTypeEnum} from '../../enums/source-type.enum';
import {
  SubmitVerificationCodeRequestModel,
  SubmitVerificationCodeResponseModel
} from '../../models/authentication/submit-verification-code.model';
import {
  SubmitVerificationCodeRequestDto,
  SubmitVerificationCodeResponseDto
} from '../../dtos/authentication/submit-verification-code.dto';
import {
  GetVerificationCodeRequestDto,
  GetVerificationCodeResponseDto
} from '../../dtos/authentication/get-verification-code.dto';
import {
  GetVerificationCodeRequestModel,
  GetVerificationCodeResponseModel
} from '../../models/authentication/get-verification-code.model';
import {UserAuthenticationBaseResponseDto} from '../../dtos/user-authentication-base.dto';
import {
  ReviewSessionCreateRequestDto,
  ReviewSessionGetAllResponseDto,
  ReviewSessionGetResponseDto,
  ReviewSessionItemGetResponseDto,
  ReviewSessionItemUpdateRequestDto, ReviewSessionSourceGetResponseDto, ReviewSessionUpdateRequestDto
} from '../../dtos/review-session.dto';
import {
  ReviewSessionCreateModel,
  ReviewSessionItemModel, ReviewSessionItemUpdateModel,
  ReviewSessionListModel,
  ReviewSessionModel,
  ReviewSessionSourceModel, ReviewSessionUpdateModel
} from '../../models/review-session.model';

export const CommonAutoMapperProfile: MappingProfile = (mapper) => {

  //#region authentication
  createMap(mapper, UserAuthenticationBaseResponseDto, UserLocalDataModel);

  createMap(mapper, GetVerificationCodeRequestModel, GetVerificationCodeRequestDto);
  createMap(
    mapper,
    SubmitVerificationCodeResponseDto,
    SubmitVerificationCodeResponseModel,
    forMember(
      dest => dest.data,
      mapFrom(src =>
        src.data
          ? mapper.map(src.data, UserAuthenticationBaseResponseDto, UserLocalDataModel)
          : null
      )
    )
  );

  createMap(mapper, SubmitVerificationCodeRequestModel, SubmitVerificationCodeRequestDto);
  createMap(mapper, SubmitVerificationCodeResponseDto, SubmitVerificationCodeResponseModel);

  createMap(mapper, SignUpRequestModel, SignUpRequestDto);
  createMap(mapper, SignUpResponseDto, SignUpResponseModel);

  createMap(mapper, RefreshTokenResponseDto, UserLocalDataModel);
  //#endregion authentication

  //#region source
  createMap(mapper, SourceListModel, SourceGetRequestDto);
  createMap(mapper, SourceGetAllResponseDto, SourceListModel,
    forMember(
      (dest) => dest.type,
      mapFrom((src) => src.type as SourceTypeEnum)
    ));
  createMap(mapper, SourceGetResponseDto, SourceModel,
    forMember(
      (dest) => dest.type,
      mapFrom((src) => src.type as SourceTypeEnum)
    ),
    forMember(
      (dest) => dest.reviewFrequencyWeightType,
      mapFrom((src) => src.reviewFrequencyWeightType)
    ));
  createMap(mapper, SourceModel, SourceCreateRequestDto,
    forMember(
      (dest) => dest.type,
      mapFrom((src) => src.type as SourceTypeEnum)
    ),
    forMember(
      (dest) => dest.reviewFrequencyWeightType,
      mapFrom((src) => src.reviewFrequencyWeightType)
    ));
  createMap(mapper, SourceModel, SourceUpdateRequestDto,
    forMember(
      (dest) => dest.type,
      mapFrom((src) => src.type as SourceTypeEnum)
    ),
    forMember(
      (dest) => dest.reviewFrequencyWeightType,
      mapFrom((src) => src.reviewFrequencyWeightType)
    ));
  createMap(mapper, SourceCreateResponseDto, SourceModel);
  createMap(mapper, SourceUpdateResponseDto, SourceModel);
  //#endregion source

  //#region highlight
  createMap(mapper, HighlightGetAllResponseDto, HighlightListModel);
  createMap(mapper, HighlightGetResponseDto, HighlightModel);
  createMap(mapper, HighlightModel, HighlightCreateRequestDto);
  createMap(mapper, HighlightModel, HighlightUpdateRequestDto);
  createMap(mapper, HighlightCreateResponseDto, HighlightModel);
  createMap(mapper, HighlightUpdateResponseDto, HighlightModel);
  //#endregion highlight

  //#region reminderLog
  createMap(mapper, ReminderLogGetAllResponseDto, ReminderLogListModel);
  createMap(mapper, ReminderLogGetResponseDto, ReminderLogModel);
  createMap(mapper, ReminderLogModel, ReminderLogCreateRequestDto);
  createMap(mapper, ReminderLogModel, ReminderLogUpdateRequestDto);
  //#endregion reminderLog

  //#region user
  createMap(mapper, UserGetAllResponseDto, UserListModel);
  createMap(mapper, UserGetResponseDto, UserModel);
  createMap(mapper, UserModel, UserCreateRequestDto);
  createMap(mapper, UserModel, UserUpdateRequestDto);
  //#endregion user

  //#region userSetting
  createMap(mapper, UserSettingGetAllResponseDto, UserSettingListModel);
  createMap(mapper, UserSettingGetResponseDto, UserSettingModel);
  createMap(mapper, UserSettingModel, UserSettingCreateRequestDto);
  createMap(mapper, UserSettingModel, UserSettingUpdateRequestDto);
  createMap(mapper, UserSettingCreateResponseDto, UserSettingModel);
  createMap(mapper, UserSettingUpdateResponseDto, UserSettingModel);
  //#endregion userSetting

  //#region reviewSession
  createMap(mapper, ReviewSessionGetAllResponseDto, ReviewSessionListModel);

  createMap(mapper, ReviewSessionGetResponseDto, ReviewSessionModel,
    forMember(d => d.items, mapFrom(s =>
      mapper.mapArray(s.items, ReviewSessionItemGetResponseDto, ReviewSessionItemModel))),
    forMember(d => d.sources, mapFrom(s =>
      mapper.mapArray(s.sources, ReviewSessionSourceGetResponseDto, ReviewSessionSourceModel)))
  );

  createMap(mapper, ReviewSessionItemGetResponseDto, ReviewSessionItemModel);
  createMap(mapper, ReviewSessionSourceGetResponseDto, ReviewSessionSourceModel);

  createMap(mapper, ReviewSessionCreateModel, ReviewSessionCreateRequestDto);
  createMap(mapper, ReviewSessionUpdateModel, ReviewSessionUpdateRequestDto);
  createMap(mapper, ReviewSessionItemUpdateModel, ReviewSessionItemUpdateRequestDto);

  // createMap(mapper, ReviewSessionModel, ReviewSessionCreateRequestDto);
  //#endregion reviewSession
}
